import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Comment } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { useHistory } from 'react-router-dom';
import useStyles from '../styles';
import feePaymentTypeOptions from '../../../utils/feePayments';
import CommentsModal from './CommentsModal';
import TermsDialog from './TermsDialog';
import SendConfirmModal from './SendConfirmModal';
import EnableEnrollmentModal from './EnableEnrollmentModal';
import { DEFAULT_STUDY_PLAN_TUITION, DiscountsProps } from '../types';
import { ReduxState } from '../../../types';
import {
  ADMINISTRATOR,
  REPRESENTATIVE,
  SECRETARY,
  STUDENT,
} from '../../../utils/user_types';
import CardPrice from './CardPrice';
import { putStudyPlan, putAcceptStudyPlan, putEnableEnrollment } from '../../../requests/api/students';
import handleApiResponse from '../../../utils/handleApiResponse';
import { ACCEPTED } from '../../../utils/contract';
import { PaymentStatus } from '../../../utils/payments';
import { formatDateTimeLong } from '../../../utils/formatters';

const agreementOptions = [
  { id: 'Embajador', name: 'Embajador' },
  { id: 'Ex-alumno', name: 'Ex-alumno' },
  { id: 'Colegio convenio', name: 'Colegio convenio' },
  { id: 'Segundo hermano', name: 'Segundo hermano' },
  { id: 'Otro', name: 'Otro' },
];

function Discounts({
  contract,
  studentId,
  onContractUpdated = () => { },
  isEnrolled = false,
  hasPendingTuitionPaymentDocument = false,
}: DiscountsProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const account = useSelector((state: ReduxState) => state.account);
  const canManagePrograms = account?.user?.userType === ADMINISTRATOR
    || account?.user?.userType === SECRETARY;
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([]);
  const [commentsModalOpen, setCommentsModalOpen] = useState<boolean>(false);
  const commentCount = contract?.contractComments?.length || 0;
  const [fee, setFee] = useState<number>(0);
  const [discountValidDays, setDiscountValidDays] = useState<number>(0);
  const [isSending, setIsSending] = useState<boolean>(false);

  // Estados para Arancel
  const [feeAgreementDiscount, setFeeAgreementDiscount] = useState<string>('0');
  const [feeAdditionalDiscount, setFeeAdditionalDiscount] = useState<string>('0');
  const [disableProgramsDiscount, setDisableProgramsDiscount] = useState<boolean>(false);

  // Estados para Matrícula
  const [matriculaValor, setMatriculaValor] = useState<string>(String(DEFAULT_STUDY_PLAN_TUITION));
  const [matriculaDescuento, setMatriculaDescuento] = useState<string>('0');
  const [matriculaConvenio, setMatriculaConvenio] = useState<string>('');

  // Estados para aceptación del plan de estudios (solo representante)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [termsModalOpen, setTermsModalOpen] = useState<boolean>(false);
  const [acceptingStudyPlan, setAcceptingStudyPlan] = useState<boolean>(false);

  // Confirmación previa al envío del plan de estudios (solo administrador/secretaria)
  const [sendConfirmModalOpen, setSendConfirmModalOpen] = useState<boolean>(false);

  // Habilitación manual del alumno como matriculado (solo administrador/secretaria)
  const [enableEnrollmentModalOpen, setEnableEnrollmentModalOpen] = useState<boolean>(false);
  const [enablingEnrollment, setEnablingEnrollment] = useState<boolean>(false);

  const initializedContractIdRef = useRef<number | undefined>(undefined);

  const handleOpenCommentsModal = () => {
    onContractUpdated();
    setCommentsModalOpen(true);
  };

  const handleCloseCommentsModal = () => {
    setCommentsModalOpen(false);
  };

  const handleCommentAdded = () => {
    setCommentsModalOpen(false);
  };

  const handleOpenTermsModal = () => {
    setTermsModalOpen(true);
  };

  const handleCloseTermsModal = () => {
    setTermsModalOpen(false);
  };

  const handleGoToPayment = () => {
    history.push(`/inscripcion/alumno/${studentId}/pago`);
  };

  const handleAcceptStudyPlan = async () => {
    if (!termsAccepted) {
      enqueueSnackbar('Debes aceptar los términos y condiciones primero', { variant: 'warning' });
      return;
    }

    setAcceptingStudyPlan(true);

    try {
      const result = await putAcceptStudyPlan(studentId);

      onContractUpdated?.();
      enqueueSnackbar('Plan de estudios aceptado exitosamente', { variant: 'success' });

      setTimeout(() => {
        if (result?.enrolledImmediately) {
          window.location.href = `/alumnos/${studentId}`;
        } else {
          history.push(`/inscripcion/alumno/${studentId}/pago`);
        }
      }, 1000);
    } catch (err) {
      const e = err as AxiosError;

      if (e.response?.status === 400 && e.response?.data) {
        const errorData = e.response.data as { message?: string; errorMessage?: string };
        const errorMessage = errorData.message || errorData.errorMessage;

        if (errorMessage) {
          enqueueSnackbar(errorMessage, {
            variant: 'error',
            autoHideDuration: 6000,
          });
          return;
        }
      }

      handleApiResponse(enqueueSnackbar, e, false);
    } finally {
      setAcceptingStudyPlan(false);
    }
  };

  const validateStudyPlanBeforeSend = (): boolean => {
    // Validar que exista un contrato
    if (!contract) {
      enqueueSnackbar('No hay plan de estudios para enviar. Por favor, crea un plan de estudios primero.', {
        variant: 'warning',
      });
      return false;
    }

    const allPrograms = contract.studentPrograms || [];
    if (allPrograms.length === 0) {
      enqueueSnackbar('Debe agregar al menos un programa al plan de estudios', {
        variant: 'error',
      });
      return false;
    }

    const tuitionValue = (matriculaValor === '' || matriculaValor == null) ? 0 : Number(matriculaValor);
    if (Number.isNaN(tuitionValue) || tuitionValue < 0) {
      enqueueSnackbar('Debe ingresar un valor de matrícula igual o mayor a 0', {
        variant: 'error',
      });
      return false;
    }

    const tuitionDiscountValue = Number(matriculaDescuento);
    if (tuitionDiscountValue > 100) {
      enqueueSnackbar('El descuento de matrícula no puede ser mayor a 100%', {
        variant: 'error',
      });
      return false;
    }

    const additionalDiscount = Number(feeAdditionalDiscount);
    if (additionalDiscount < 0) {
      enqueueSnackbar('El descuento adicional del arancel no puede ser negativo', {
        variant: 'error',
      });
      return false;
    }

    if (additionalDiscount > fee) {
      enqueueSnackbar('El descuento adicional no puede superar al arancel total', {
        variant: 'error',
      });
      return false;
    }

    if (discountValidDays && discountValidDays < 0) {
      enqueueSnackbar('Los días válidos de descuento no pueden ser negativos', {
        variant: 'error',
      });
      return false;
    }

    // Validar que los programas tengan sección asignada
    const programsWithoutSection = allPrograms.filter(
      (program) => program.sectionId === null || program.sectionId === undefined,
    );

    if (programsWithoutSection.length > 0) {
      const programNames = programsWithoutSection
        .map((p) => p.program.name)
        .join(', ');
      enqueueSnackbar(
        `Los siguientes programas no tienen una sección asignada: ${programNames}. Por favor, asigne una sección antes de enviar el plan de estudios.`,
        {
          variant: 'error',
          autoHideDuration: 6000,
        },
      );
      return false;
    }

    return true;
  };

  const handleConfirmEnableEnrollment = async () => {
    setEnablingEnrollment(true);

    try {
      await putEnableEnrollment(studentId);
      enqueueSnackbar('Alumno habilitado como matriculado', { variant: 'success' });
      setEnableEnrollmentModalOpen(false);
      // El stage del alumno lo tienen vistas superiores (tabs, encabezado): recargamos
      // para que toda la pantalla quede consistente.
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      const e = err as AxiosError;
      const errorData = e.response?.data as { message?: string; errorMessage?: string } | undefined;
      const errorMessage = errorData?.message || errorData?.errorMessage;

      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } else {
        handleApiResponse(enqueueSnackbar, e, false);
      }
      setEnablingEnrollment(false);
    }
  };

  const handleRequestSendStudyPlan = () => {
    if (!validateStudyPlanBeforeSend()) {
      return;
    }
    setSendConfirmModalOpen(true);
  };

  const handleConfirmSendStudyPlan = async () => {
    if (!contract) {
      return;
    }

    const allPrograms = contract.studentPrograms || [];
    const tuitionValue = (matriculaValor === '' || matriculaValor == null) ? 0 : Number(matriculaValor);
    const tuitionDiscountValue = Number(matriculaDescuento);
    const additionalDiscount = Number(feeAdditionalDiscount);

    setIsSending(true);

    try {
      // Calcular feeDiscount y feeDiscountType
      const agreementDiscount = Number(feeAgreementDiscount);
      let feeDiscount = 0;
      let feeDiscountType: string | null = null;

      if (agreementDiscount > 0) {
        feeDiscount = agreementDiscount;
        feeDiscountType = 'agreement';
      } else if (!disableProgramsDiscount && programsDiscount > 0) {
        feeDiscount = programsDiscount;
        feeDiscountType = 'programs';
      }

      const studentProgramInfo = allPrograms.map((program) => ({
        programId: String(program.programId),
        feeDiscount: program.feeDiscount,
        comment: program.comment || '',
      }));

      await putStudyPlan({
        contractId: String(contract.id),
        studentId,
        startDate: contract.startDate instanceof Date
          ? contract.startDate
          : new Date(contract.startDate),
        endDate: contract.endDate instanceof Date
          ? contract.endDate
          : new Date(contract.endDate),
        tuition: tuitionValue,
        tuitionDiscount: tuitionDiscountValue,
        tuitionAgreement: matriculaConvenio || null,
        feeDiscount,
        feeDiscountType,
        feeAdditionalDiscount: additionalDiscount,
        feePaymentTypeDiscount: 0,
        feeDiscountInvalidPaymentMethods: selectedPaymentMethods.length > 0
          ? JSON.stringify(selectedPaymentMethods)
          : null,
        feeDiscountValidDays: discountValidDays > 0 ? discountValidDays : null,
        studentProgramInfo,
        flow: 'study-plan',
      });

      enqueueSnackbar('Plan de estudios actualizado exitosamente', {
        variant: 'success',
      });

      // Llamar al callback para refrescar datos si existe
      if (onContractUpdated) {
        onContractUpdated();
      }
    } catch (err) {
      const e = err as AxiosError;
      if (e.response?.data) {
        const errorData = e.response.data as { message?: string; errorMessage?: string };
        const errorMessage = errorData.message || errorData.errorMessage;

        if (errorMessage) {
          enqueueSnackbar(errorMessage, { variant: 'error' });
        } else {
          handleApiResponse(enqueueSnackbar, e, false);
        }
      } else {
        handleApiResponse(enqueueSnackbar, e, false);
      }
    } finally {
      setIsSending(false);
      setSendConfirmModalOpen(false);
    }
  };

  useEffect(() => {
    if (contract) {
      let totalFee = 0;
      contract.studentPrograms?.forEach((program) => {
        totalFee += program.program.fee - program.feeDiscount;
      });
      setFee(totalFee);
    }
  }, [contract]);

  useEffect(() => {
    if (!contract) return;
    if (contract.id === initializedContractIdRef.current) return;

    initializedContractIdRef.current = contract.id;

    const rawTuition = contract.tuition;
    // Una matrícula guardada en 0 es un valor válido (matrícula gratis): solo caemos al
    // valor por defecto cuando el contrato no tiene monto guardado.
    const displayTuition = rawTuition ?? DEFAULT_STUDY_PLAN_TUITION;
    setMatriculaValor(String(displayTuition));
    setMatriculaDescuento(String((rawTuition || 0) === 0 ? 0 : (contract.tuitionDiscount || 0)));
    setMatriculaConvenio(contract.tuitionAgreement || '');

    if (contract.feeDiscountType === 'agreement') {
      setFeeAgreementDiscount(String(contract.feeDiscount || '0'));
    } else {
      setFeeAgreementDiscount('0');
    }
    setFeeAdditionalDiscount(String(contract.feeAdditionalDiscount || '0'));

    setDisableProgramsDiscount(contract.feeDiscountType !== 'programs');

    setDiscountValidDays(contract.feeDiscountValidDays || 0);

    if (contract.feeDiscountInvalidPaymentMethods) {
      try {
        const parsedMethods = JSON.parse(contract.feeDiscountInvalidPaymentMethods);
        if (Array.isArray(parsedMethods)) {
          setSelectedPaymentMethods(parsedMethods);
        }
      } catch {
        setSelectedPaymentMethods([]);
      }
    } else {
      setSelectedPaymentMethods([]);
    }
  }, [contract]);

  useEffect(() => {
    // Selecting a convenio defaults the discount to 50%, but only when the user
    // hasn't already entered a custom value; a manually-entered value prevails.
    if (matriculaConvenio && matriculaConvenio !== '') {
      if (!matriculaDescuento || matriculaDescuento === '0') {
        setMatriculaDescuento('50');
      }
    }
  }, [matriculaConvenio]);

  useEffect(() => {
    const agreementValue = Number(feeAgreementDiscount);
    const programCount = contract?.studentPrograms?.length || 0;

    if (agreementValue > 0) {
      setDisableProgramsDiscount(true);
    } else if (programCount < 2) {
      setDisableProgramsDiscount(false);
    }
  }, [feeAgreementDiscount, contract]);

  const programsDiscount = (() => {
    const programCount = contract?.studentPrograms?.length || 0;
    if (programCount >= 3) {
      return 10;
    }
    if (programCount === 2) {
      return 5;
    }
    return 0;
  })();

  const programsDiscountLabel = (() => {
    const programCount = contract?.studentPrograms?.length || 0;
    if (programCount >= 3) {
      return '3 o más programas - 10% de descuento';
    }
    if (programCount === 2) {
      return '2 programas - 5% de descuento';
    }
    return 'Sin descuento (requiere 2 o más programas)';
  })();

  const calculateFinalFee = () => {
    let totalDiscount = 0;

    if (Number(feeAgreementDiscount) > 0) {
      totalDiscount += fee * (Number(feeAgreementDiscount) / 100);
    }

    if (!disableProgramsDiscount && programsDiscount > 0) {
      totalDiscount += fee * (programsDiscount / 100);
    }

    const additionalDiscount = Number(feeAdditionalDiscount);
    if (additionalDiscount > 0) {
      totalDiscount += Math.min(additionalDiscount, fee - totalDiscount);
    }

    return Math.max(0, fee - totalDiscount);
  };

  const calculateFinalTuition = () => {
    const tuition = Number(matriculaValor);
    const discount = Number(matriculaDescuento);
    return Math.max(0, tuition * (1 - discount / 100));
  };

  const renderCommentButton = () => {
    if (!contract) return null;

    return (
      <Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Comment />}
          onClick={handleOpenCommentsModal}
          className={classes.commentsButton}
        >
          Ver comentarios (
          {commentCount}
          )
        </Button>
        <CommentsModal
          open={commentsModalOpen}
          onClose={handleCloseCommentsModal}
          contractId={contract.id}
          comments={contract.contractComments || []}
          onCommentAdded={handleCommentAdded}
        />
      </Box>
    );
  };

  const renderTermsCheckbox = () => (
    <FormControlLabel
      control={
        <Checkbox
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          color="primary"
        />
      }
      label={
        <Box className={classes.termsTextContainer}>
          <Typography className={classes.termsText}>
            He leído y acepto los{' '}
            <span
              className={classes.termsTextLink}
              onClick={handleOpenTermsModal}
            >
              términos y condiciones
            </span>
          </Typography>
        </Box>
      }
    />
  );

  const renderAcceptButton = () => (
    <Button
      variant="contained"
      color="primary"
      onClick={handleAcceptStudyPlan}
      disabled={!termsAccepted || acceptingStudyPlan}
      className={classes.fullWidthButton}
      fullWidth
    >
      {acceptingStudyPlan ? 'Aceptando...' : 'Aceptar plan de estudios'}
    </Button>
  );

  // Habilitar al alumno solo tiene sentido cuando la matrícula ya no es deuda: monto 0,
  // o pagada (aunque el apoderado no haya aceptado el plan). Espeja la validación del
  // backend en enrollment.services::enableStudentEnrollment.
  const canEnableEnrollment = (() => {
    if (isEnrolled || !contract) {
      return false;
    }

    const tuitionAmount = Math.round(
      (contract.tuition || 0) * (1 - (contract.tuitionDiscount || 0) / 100),
    );
    if (tuitionAmount === 0) {
      return true;
    }

    const tuitionPayments = contract.contractPayments || [];
    const hasPendingTuition = tuitionPayments.some(
      (payment) => payment.status !== PaymentStatus.PAID,
    );
    const hasPaidTuition = tuitionPayments.some(
      (payment) => payment.status === PaymentStatus.PAID,
    );

    return hasPaidTuition && !hasPendingTuition;
  })();

  const proposalSentLabel = formatDateTimeLong(contract?.proposalSentAt);
  const proposalAcceptedLabel = formatDateTimeLong(contract?.proposalAcceptedAt);

  if (!canManagePrograms) {
    if (account?.user?.userType === STUDENT) {
      return (
        <Box>
          {renderCommentButton()}
        </Box>
      );
    }

    return (
      <Box className={classes.card}>
        <Typography className={classes.cardTitle}>
          Arancel
        </Typography>
        <Box className={classes.innerSection}>
          <CardPrice
            title="Valor final arancel"
            originalPrice={fee}
            discountPrice={fee - calculateFinalFee()}
            finalPrice={calculateFinalFee()}
          />
        </Box>

        <Divider />

        <Typography className={classes.cardTitle}>
          Matrícula
        </Typography>
        <Box className={classes.innerSection}>
          <CardPrice
            title="Valor final matrícula"
            originalPrice={Number(matriculaValor)}
            discountType="percentage"
            discountPrice={Number(matriculaDescuento)}
            finalPrice={calculateFinalTuition()}
          />
        </Box>

        {contract && (
          <>
            {renderCommentButton()}
            {account?.user?.userType === REPRESENTATIVE && contract.status !== ACCEPTED && (
              <>
                <Divider className={classes.innerSection} />
                <Box className={classes.innerSection}>
                  {renderTermsCheckbox()}
                </Box>
                {renderAcceptButton()}
              </>
            )}
            {account?.user?.userType === REPRESENTATIVE
              && contract.status === ACCEPTED
              && !isEnrolled
              && (
                <>
                  <Divider className={classes.innerSection} />
                  <Box className={classes.innerSection}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.fullWidthButton}
                      fullWidth
                      onClick={handleGoToPayment}
                      disabled={hasPendingTuitionPaymentDocument}
                    >
                      {hasPendingTuitionPaymentDocument
                        ? 'Documento pendiente de validación'
                        : 'Pagar Matrícula'}
                    </Button>
                  </Box>
                </>
              )}
            <TermsDialog
              open={termsModalOpen}
              onClose={handleCloseTermsModal}
              onAccept={() => {
                setTermsAccepted(true);
                handleCloseTermsModal();
              }}
            />
          </>
        )}
      </Box>
    );
  }

  const isPlanAccepted = contract?.status === ACCEPTED;

  return (
    <Box className={classes.card}>
      <Typography className={classes.cardTitle}>
        Arancel
      </Typography>
      <Box className={classes.innerSection}>
        <TextField
          fullWidth
          label="Convenio (%)"
          variant="outlined"
          type="number"
          inputProps={{ min: 0, max: 100 }}
          value={feeAgreementDiscount}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value >= 0 && value <= 100) {
              setFeeAgreementDiscount(e.target.value);
            }
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          className={classes.inputCurrency}
          disabled={isPlanAccepted}
        />
      </Box>

      <Box className={classes.innerSection}>
        <TextField
          fullWidth
          label="Descuento (opcional)"
          variant="outlined"
          type="number"
          inputProps={{ min: 0 }}
          value={feeAdditionalDiscount}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value < 0) {
              return;
            }
            if (value > fee) {
              return;
            }
            setFeeAdditionalDiscount(e.target.value);
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">
              <Typography>
                $
              </Typography>
            </InputAdornment>,
          }}
          className={classes.inputCurrency}
          helperText={Number(feeAdditionalDiscount) > fee ? 'El descuento no puede superar al arancel' : ''}
          error={Number(feeAdditionalDiscount) > fee}
          disabled={isPlanAccepted}
        />
      </Box>
      <Box className={classes.innerSection}>
        <TextField
          fullWidth
          label="Descuento por N° de programas"
          variant="outlined"
          value={programsDiscountLabel}
          InputProps={{
            readOnly: true,
          }}
          className={classes.inputCurrency}
          disabled
        />
      </Box>
      {programsDiscount > 0 && (
        <Box className={classes.innerSection}>
          <FormControlLabel
            control={
              <Checkbox
                checked={disableProgramsDiscount}
                onChange={(e) => setDisableProgramsDiscount(e.target.checked)}
                color="primary"
                disabled={isPlanAccepted}
              />
            }
            label="Desactivar descuento por N° de programas"
          />
        </Box>
      )}
      <Box className={classes.innerSection}>
        <CardPrice
          title="Valor final arancel"
          originalPrice={fee}
          discountPrice={fee - calculateFinalFee()}
          finalPrice={calculateFinalFee()}
        />
      </Box>

      <Divider />

      <Typography className={classes.cardTitle}>
        Matrícula
      </Typography>

      <Box className={classes.innerSection}>
        <TextField
          fullWidth
          label="Valor"
          variant="outlined"
          type="number"
          inputProps={{ min: 0 }}
          value={matriculaValor}
          onChange={({ target: { value } }) => {
            setMatriculaValor(value);
            if (Number(value) === 0) {
              setMatriculaDescuento('0');
            }
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">
              <Typography>
                $
              </Typography>
            </InputAdornment>,
          }}
          className={classes.inputCurrency}
          disabled={isPlanAccepted}
        />
      </Box>

      <Box className={classes.innerSection}>
        <FormControl fullWidth variant="outlined" className={classes.inputCurrency} disabled={isPlanAccepted}>
          <InputLabel>Convenio</InputLabel>
          <Select
            value={matriculaConvenio}
            onChange={(e) => setMatriculaConvenio(e.target.value)}
            label="Convenio"
            size="small"
            disabled={isPlanAccepted}
          >
            <MenuItem value="">
              <em>Ninguno</em>
            </MenuItem>
            {agreementOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box className={classes.innerSection}>
        <TextField
          fullWidth
          label="Descuento (%)"
          variant="outlined"
          type="number"
          inputProps={{ min: 0, max: 100 }}
          value={matriculaDescuento}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value >= 0 && value <= 100) {
              setMatriculaDescuento(e.target.value);
            }
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          className={classes.inputCurrency}
          disabled={isPlanAccepted}
        />
      </Box>

      <Box className={classes.innerSection}>
        <CardPrice
          title="Valor final matrícula"
          originalPrice={Number(matriculaValor)}
          discountType="percentage"
          discountPrice={Number(matriculaDescuento)}
          finalPrice={calculateFinalTuition()}
        />
      </Box>
      <Box className={classes.discountsContainer}>
        <Divider />
        <Typography className={classes.cardTitle}>
          Descuentos válidos por:
        </Typography>
        <Box className={classes.innerSection}>
          <TextField
            type="number"
            value={discountValidDays}
            label="Días"
            onChange={(e) => {
              const { value } = e.target;
              if (value === '' || /^\d+$/.test(value)) {
                setDiscountValidDays(Number(value));
              }
            }}
            placeholder="0"
            className={classes.inputCurrency}
            disabled={isPlanAccepted}
          />
        </Box>
        <Typography className={classes.cardTitle}>
          Descuentos válido pagando con:
        </Typography>
        <Box className={classes.paymentMethodsContainer}>
          {feePaymentTypeOptions.map((paymentMethod) => (
            <Box key={paymentMethod.id} className={classes.paymentMethodItem}>
              <Checkbox
                checked={selectedPaymentMethods.includes(paymentMethod.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedPaymentMethods([
                      ...selectedPaymentMethods,
                      paymentMethod.id,
                    ]);
                  } else {
                    setSelectedPaymentMethods(
                      selectedPaymentMethods.filter((id) => id !== paymentMethod.id),
                    );
                  }
                }}
                color="primary"
                disabled={isPlanAccepted}
              />
              <Typography className={classes.paymentMethodLabel}>
                {paymentMethod.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {contract && renderCommentButton()}
      {!isPlanAccepted && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleRequestSendStudyPlan}
          className={classes.fullWidthButton}
          disabled={isSending || !contract}
        >
          {isSending ? 'Enviando...' : 'Enviar Plan de Estudios'}
        </Button>
      )}
      {proposalSentLabel && (
        <Typography className={classes.planStatusMessage}>
          {`Plan de estudios enviado el ${proposalSentLabel}`}
        </Typography>
      )}
      {isPlanAccepted && proposalAcceptedLabel && (
        <Typography className={classes.planStatusMessage}>
          {`El apoderado aceptó el plan de estudios el ${proposalAcceptedLabel}`}
        </Typography>
      )}
      {canEnableEnrollment && (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setEnableEnrollmentModalOpen(true)}
          className={classes.secondaryFullWidthButton}
          disabled={enablingEnrollment}
        >
          Habilitar alumno
        </Button>
      )}
      <SendConfirmModal
        open={sendConfirmModalOpen}
        onClose={() => setSendConfirmModalOpen(false)}
        onConfirm={handleConfirmSendStudyPlan}
        loading={isSending}
      />
      <EnableEnrollmentModal
        open={enableEnrollmentModalOpen}
        onClose={() => setEnableEnrollmentModalOpen(false)}
        onConfirm={handleConfirmEnableEnrollment}
        loading={enablingEnrollment}
      />
    </Box>
  );
}

export default Discounts;
